import { Avatar, Box, IconButton, Menu, MenuItem, Typography } from '@mui/material'
import {
  type CommentModel,
  type UpdateCommentValues,
  type OrganizationCommentGroupModel,
  CommentParentType
} from '../../../models/Comment'
import { MoreHoriz } from '@mui/icons-material'
import { useState, type MouseEvent } from 'react'
import { UpdateComment } from '../../../services/CommentService'
import { toast } from 'react-toastify'
import { generatePath } from 'react-router'
import { AppRoutes } from '../../Routes'
import { useNavigate, useParams } from 'react-router-dom'
import { AnnualPlanTabs } from './PlanningDetails'
import {
  GetPlanOrganizationTaskById
} from '../../../services/AnnualPlanService'
import { PlanOrganizationTaskName } from '../../../models/PlanOrganizationTask'
import { handleApiResponse } from '../../../utils/apiHelpers'

interface Props {
  organizationCommentModel: OrganizationCommentGroupModel
  onCommentUpdate: (updatedComment: CommentModel) => void
  changeTab: (tab: number, showComments?: boolean) => void
}

export const OrganizationTasksRecentComments = (props: Props): JSX.Element => {
  const { rcnId, planId } = useParams()
  const nav = useNavigate()

  const [selectedComment, setSelectedComment] = useState<CommentModel | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const actionsMenuOpen = Boolean(anchorEl)

  const navigateToPlanOrganizationTask = async (planOrganizationTaskId: number, openComments: boolean = false): Promise<void> => {
    if (rcnId == null || planId == null) return

    const planOrganizationTask = await handleApiResponse(GetPlanOrganizationTaskById(planOrganizationTaskId))
    if (planOrganizationTask == null) return

    if (planOrganizationTask.taskName === PlanOrganizationTaskName.RcnOperations) {
      props.changeTab(Number(AnnualPlanTabs.RcnOperations), openComments)
      return
    }

    if (planOrganizationTask.taskName === PlanOrganizationTaskName.BudgetOverview) {
      props.changeTab(Number(AnnualPlanTabs.BudgetOverview), openComments)
      return
    }

    if (planOrganizationTask.taskName === PlanOrganizationTaskName.SignContract) {
      props.changeTab(Number(AnnualPlanTabs.SignContract), openComments)
      return
    }

    const planOrgId = String(planOrganizationTask.planOrganizationId)
    const planOrgTaskId = String(planOrganizationTask.id)
    const route = generatePath(
      AppRoutes.PLANNING_PRIORITY_ACTIVITIES_PLAN_ORGANIZATION_PRIORITY,
      {
        rcnId,
        planId,
        tab: String(AnnualPlanTabs.PriorityActivities),
        planOrgId,
        planOrgTaskId
      }
    )
    nav(route + (openComments ? '?comments=true' : ''))
  }

  const handleMenuClick = (e: MouseEvent<HTMLButtonElement>, comment: CommentModel): void => {
    setAnchorEl(e.currentTarget)
    setSelectedComment(comment)
  }

  const handleMenuClose = (): void => {
    setAnchorEl(null)
    setSelectedComment(null)
  }

  const handleAddComment = (comment: CommentModel): void => {
    switch (comment.parentType) {
      case CommentParentType.Comment: {
        break
      }
      case CommentParentType.PlanOrganizationTask: {
        void navigateToPlanOrganizationTask(comment.parentId, true)
        break
      }
    }
    handleMenuClose()
  }

  const handleViewActivity = async (comment: CommentModel): Promise<void> => {
    switch (comment.parentType) {
      case CommentParentType.Comment: {
        break
      }
      case CommentParentType.PlanOrganizationTask: {
        void navigateToPlanOrganizationTask(comment.parentId)
        break
      }
    }
    handleMenuClose()
  }

  const handleResolve = async (comment: CommentModel): Promise<void> => {
    const updateCommentValues: UpdateCommentValues = {
      id: comment.id,
      commentText: comment.commentText,
      isResolved: !comment.isResolved
    }

    const { success, errors } = await UpdateComment(updateCommentValues)

    if (!success) {
      errors.forEach((e) => toast.error(e))
      return
    }

    comment.isResolved = !comment.isResolved
    props.onCommentUpdate(comment)

    handleMenuClose()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {props.organizationCommentModel.taskGroups.map(taskGroup => {
        return <Box key={taskGroup.taskId}>
          <Box sx={{ backgroundColor: 'var(--blue-50)', borderWidth: '1px 0 1px 0', borderColor: 'var(--blue-300)', borderStyle: 'solid', color: 'var(--grey-900)', textAlign: 'center' }}>
            <Typography variant='h5' padding='10px'>
              {props.organizationCommentModel.organizationName} - {taskGroup.taskName}
            </Typography>
          </Box>

          {taskGroup.comments.map(comment => {
            return <Box data-testid='orgTaskRecentComment' key={comment.id} sx={{ padding: '12px', display: 'flex', gap: '12px' }}>
              <Avatar src={comment.user?.imageKey} sx={{ width: '32px', height: '32px', marginTop: '10px' }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant='h5'>
                    {comment.user?.firstName} {comment.user?.lastName}
                  </Typography>

                  <IconButton
                    aria-controls={actionsMenuOpen ? 'comment-actions-menu' : undefined}
                    aria-haspopup='true'
                    aria-expanded={actionsMenuOpen ? 'true' : undefined}
                    aria-label='actions menu'
                    data-testid='actionButton'
                    onClick={(e) => { handleMenuClick(e, comment) }}
                  >
                    <MoreHoriz />
                  </IconButton>
                </Box>

                <Typography variant='body1' sx={{ color: 'var(--grey-500)' }}>
                  {new Date(comment.dateStamp).toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>

                <Typography variant='body1' dangerouslySetInnerHTML={{ __html: comment.commentText }} />
              </Box>
            </Box>
          })}

        </Box>
      })}

      {selectedComment != null &&
        <Menu
          id='comment-actions-menu'
          anchorEl={anchorEl}
          open={actionsMenuOpen}
          onClose={handleMenuClose}
          MenuListProps={{ 'aria-labelledby': 'comment-actions-button' }}
        >
          <MenuItem data-testid="addComment" onClick={() => { handleAddComment(selectedComment) }}>
            Add Comment
          </MenuItem>
          <MenuItem data-testid="viewActivity" onClick={() => { void handleViewActivity(selectedComment) }}>
            View Activity
          </MenuItem>
          <MenuItem data-testid="toggleResolve" onClick={async () => { await handleResolve(selectedComment) }}>
            Mark {selectedComment.isResolved ? 'Unresolved' : 'Resolved'}
          </MenuItem>
        </Menu>
      }
    </Box>
  )
}
